/**
 * Voice input via Cloudflare Workers AI — Whisper large v3 turbo.
 * Records microphone audio, sends base64 to /ai/transcribe.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import i18n from '../../i18n';

const st = (key, opts) => i18n.t(`modules.shared.aiAgent.speechToText.${key}`, opts);

const MAX_RECORD_MS = 45000;
const BAR_COUNT = 32;

function createIdleLevels() {
  return Array.from({ length: BAR_COUNT }, () => 0.12);
}

function pickRecorderMimeType() {
  if (typeof MediaRecorder === 'undefined') return '';
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/ogg;codecs=opus',
  ];
  return candidates.find((type) => MediaRecorder.isTypeSupported(type)) || '';
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error(st('audio_read_failed')));
        return;
      }
      const base64 = result.includes(',') ? result.split(',')[1] : result;
      resolve(base64);
    };
    reader.onerror = () => reject(new Error(st('audio_read_failed')));
    reader.readAsDataURL(blob);
  });
}

function mergeTranscript(base, chunk) {
  const left = String(base || '').trimEnd();
  const right = String(chunk || '').trim();
  if (!right) return left;
  if (!left) return right;
  return `${left} ${right}`.trim();
}

/**
 * @param {{
 *   transcribe?: (base64Audio: string) => Promise<{ text?: string } | string>;
 *   onTranscript?: (text: string, meta: { isFinal: boolean }) => void;
 *   onError?: (message: string) => void;
 * }} options
 */
export function useSpeechToText(options = {}) {
  const { transcribe, onTranscript, onError } = options;

  const onTranscriptRef = useRef(onTranscript);
  const onErrorRef = useRef(onError);
  const transcribeRef = useRef(transcribe);
  const mediaStreamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const sessionBaseRef = useRef('');
  const maxTimerRef = useRef(null);
  const isRecordingRef = useRef(false);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const recordStartRef = useRef(0);
  const skipTranscriptionRef = useRef(false);

  const [isSupported, setIsSupported] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingMs, setRecordingMs] = useState(0);
  const [audioLevels, setAudioLevels] = useState(createIdleLevels);
  const [error, setError] = useState(null);

  useEffect(() => {
    onTranscriptRef.current = onTranscript;
    onErrorRef.current = onError;
    transcribeRef.current = transcribe;
  }, [onTranscript, onError, transcribe]);

  useEffect(() => {
    const ok =
      typeof navigator !== 'undefined' &&
      !!navigator.mediaDevices?.getUserMedia &&
      typeof MediaRecorder !== 'undefined' &&
      typeof transcribe === 'function';
    setIsSupported(ok);
  }, [transcribe]);

  const stopVisualizer = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    analyserRef.current = null;
  }, []);

  const startVisualizer = useCallback((stream) => {
    stopVisualizer();
    recordStartRef.current = Date.now();
    setRecordingMs(0);

    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;

      const audioContext = new AudioCtx();
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 128;
      analyser.smoothingTimeConstant = 0.72;

      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      const freqData = new Uint8Array(analyser.frequencyBinCount);

      const tick = () => {
        if (!analyserRef.current) return;

        analyserRef.current.getByteFrequencyData(freqData);
        const step = Math.max(1, Math.floor(freqData.length / BAR_COUNT));
        const nextLevels = Array.from({ length: BAR_COUNT }, (_, i) => {
          const slice = freqData.slice(i * step, i * step + step);
          const avg =
            slice.reduce((sum, v) => sum + v, 0) / Math.max(slice.length, 1);
          return Math.min(1, Math.max(0.1, avg / 140));
        });

        setAudioLevels(nextLevels);
        setRecordingMs(Date.now() - recordStartRef.current);
        rafRef.current = requestAnimationFrame(tick);
      };

      rafRef.current = requestAnimationFrame(tick);
    } catch {
      const tick = () => {
        if (!isRecordingRef.current) return;
        setRecordingMs(Date.now() - recordStartRef.current);
        setAudioLevels(
          Array.from({ length: BAR_COUNT }, (_, i) => {
            const wave = Math.sin(Date.now() / 180 + i * 0.45) * 0.5 + 0.5;
            return 0.12 + wave * 0.35;
          })
        );
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    }
  }, [stopVisualizer]);

  const cleanupStream = useCallback(() => {
    stopVisualizer();
    if (maxTimerRef.current) {
      clearTimeout(maxTimerRef.current);
      maxTimerRef.current = null;
    }
    mediaRecorderRef.current = null;
    chunksRef.current = [];
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
  }, [stopVisualizer]);

  const runTranscription = useCallback(async (blob) => {
    const transcribeFn = transcribeRef.current;
    if (!transcribeFn) {
      throw new Error(st('cloud_unavailable'));
    }

    setIsTranscribing(true);
    try {
      const base64 = await blobToBase64(blob);
      const result = await transcribeFn(base64);
      const text =
        typeof result === 'string'
          ? result
          : result?.text || result?.transcript || '';

      if (!String(text).trim()) {
        throw new Error(st('no_speech_detected'));
      }

      const merged = mergeTranscript(sessionBaseRef.current, text);
      sessionBaseRef.current = merged;
      onTranscriptRef.current?.(merged, { isFinal: true });
      setError(null);
    } finally {
      setIsTranscribing(false);
      setRecordingMs(0);
      setAudioLevels(createIdleLevels());
    }
  }, []);

  const stopListening = useCallback(() => {
    skipTranscriptionRef.current = false;

    if (!isRecordingRef.current && !mediaRecorderRef.current) {
      cleanupStream();
      setIsListening(false);
      return;
    }

    isRecordingRef.current = false;
    setIsListening(false);

    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      try {
        recorder.stop();
      } catch {
        cleanupStream();
      }
      return;
    }

    cleanupStream();
  }, [cleanupStream]);

  const cancelListening = useCallback(() => {
    skipTranscriptionRef.current = true;
    isRecordingRef.current = false;
    setIsListening(false);

    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      try {
        recorder.stop();
      } catch {
        skipTranscriptionRef.current = false;
        cleanupStream();
        setRecordingMs(0);
        setAudioLevels(createIdleLevels());
      }
      return;
    }

    skipTranscriptionRef.current = false;
    cleanupStream();
    setRecordingMs(0);
    setAudioLevels(createIdleLevels());
  }, [cleanupStream]);

  const startListening = useCallback(
    async (currentText = '') => {
      if (!transcribeRef.current) {
        const msg = st('license_required');
        setError(msg);
        onErrorRef.current?.(msg);
        return;
      }

      if (isRecordingRef.current) {
        stopListening();
        return;
      }

      sessionBaseRef.current = String(currentText || '').trimEnd();
      setError(null);
      chunksRef.current = [];
      skipTranscriptionRef.current = false;

      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        mediaStreamRef.current = stream;
        startVisualizer(stream);

        const mimeType = pickRecorderMimeType();
        const recorder = mimeType
          ? new MediaRecorder(stream, { mimeType })
          : new MediaRecorder(stream);

        recorder.ondataavailable = (event) => {
          if (event.data?.size > 0) {
            chunksRef.current.push(event.data);
          }
        };

        recorder.onerror = () => {
          const msg = st('recording_error');
          setError(msg);
          onErrorRef.current?.(msg);
          isRecordingRef.current = false;
          setIsListening(false);
          cleanupStream();
        };

        recorder.onstop = async () => {
          const cancelled = skipTranscriptionRef.current;
          skipTranscriptionRef.current = false;

          const blob = new Blob(chunksRef.current, {
            type: recorder.mimeType || mimeType || 'audio/webm',
          });
          cleanupStream();

          if (cancelled) {
            setRecordingMs(0);
            setAudioLevels(createIdleLevels());
            return;
          }

          if (blob.size < 800) {
            const msg = st('recording_too_short');
            setError(msg);
            onErrorRef.current?.(msg);
            return;
          }

          try {
            await runTranscription(blob);
          } catch (err) {
            const msg = err?.message || st('transcription_failed');
            setError(msg);
            onErrorRef.current?.(msg);
          }
        };

        mediaRecorderRef.current = recorder;
        recorder.start(250);
        isRecordingRef.current = true;
        setIsListening(true);

        maxTimerRef.current = setTimeout(() => {
          if (isRecordingRef.current) {
            stopListening();
          }
        }, MAX_RECORD_MS);
      } catch (err) {
        cleanupStream();
        isRecordingRef.current = false;
        setIsListening(false);
        const msg =
          err?.name === 'NotAllowedError'
            ? st('mic_denied')
            : err?.message || st('mic_access_failed');
        setError(msg);
        onErrorRef.current?.(msg);
      }
    },
    [cleanupStream, runTranscription, startVisualizer, stopListening]
  );

  const toggleListening = useCallback(
    (currentText = '') => {
      if (isTranscribing) return;
      if (isRecordingRef.current) {
        stopListening();
        return;
      }
      startListening(currentText);
    },
    [isTranscribing, startListening, stopListening]
  );

  useEffect(() => {
    return () => {
      isRecordingRef.current = false;
      stopVisualizer();
      try {
        mediaRecorderRef.current?.stop();
      } catch {
        /* ignore */
      }
      cleanupStream();
    };
  }, [cleanupStream, stopVisualizer]);

  return {
    isSupported,
    isListening,
    isTranscribing,
    recordingMs,
    audioLevels,
    error,
    startListening,
    stopListening,
    cancelListening,
    toggleListening,
  };
}

export default useSpeechToText;
